// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: "Task Assignment" dialog of the Project Management view (Plan mode):
// assignees, project/task, activity type, daily duration with presets, live
// availability of the assignees and splitting around non-working days.

import {
	activityColor,
	daysBetween,
	formatDay,
	formatMinutes,
	getActivityTypeColors,
	h,
	swatch,
} from "./PMRender";

const DOCTYPE = "Task Assignment";
const AVAILABILITY_METHOD = "erpnext.projects.capacity.get_availability";
const DEFAULT_DAY_MINUTES = 8 * 60;
const MAX_AVAILABILITY_DAYS = 31;
const RECENT_TYPES_COUNT = 4;
const ASSIGN_TASK_METHOD = "erpnext.projects.project_management.assign_task_to_employees"; // @dokos

/**
 * @typedef {Object} TaskAssignmentValues
 * @property {string} [project]
 * @property {string} [task]
 * @property {string} [activity_type]
 * @property {string[]} [employees]
 * @property {string} [start_date]
 * @property {string} [end_date]
 * @property {number} [duration] seconds per day
 */

/**
 * @dokos: open the Task Assignment dialog.
 * @param {Object} opts
 * @param {string} [opts.name] existing Task Assignment to edit
 * @param {TaskAssignmentValues} [opts.values] prefill of a new assignment
 * @param {function} [opts.on_saved] called after a save or a delete
 * @returns {Promise<PMTaskAssignmentDialog>}
 */
export async function openTaskAssignmentDialog(opts = {}) {
	const dialog = new PMTaskAssignmentDialog(opts);
	await dialog.show();
	return dialog;
}

/** @dokos */
export class PMTaskAssignmentDialog {
	/** @param {{name?: string, values?: TaskAssignmentValues, on_saved?: function}} opts */
	constructor({ name = null, values = {}, on_saved = null } = {}) {
		this.name = name;
		this.values = values || {};
		this.on_saved = on_saved;
		this.doc = null;
		this.availability = null;
		this._availability_seq = 0;
	}

	get is_new() {
		return !this.name;
	}

	async show() {
		await frappe.model.with_doctype(DOCTYPE);
		if (!this.is_new) {
			this.doc = await frappe.db.get_doc(DOCTYPE, this.name);
		}
		this.make();
		this.dialog.show();
		await this.set_initial_values();
		if (this.dialog.display) this.focus_first_empty_field(); // @dokos: values may land after "shown"
		this.render_recent_types();
		this.render_presets();
		this.refresh_availability();
	}

	make() {
		const can_write = frappe.model.can_write(DOCTYPE);
		this.dialog = new frappe.ui.Dialog({
			title: this.is_new
				? __("New {0}", [__(DOCTYPE)], DOCTYPE)
				: __("Edit {0}", [__(DOCTYPE)], DOCTYPE),
			size: "large",
			fields: this.get_fields(),
			primary_action_label: __("Save"),
			primary_action: () => this.save(),
		});
		this.dialog.$wrapper.addClass("pm-ta-dialog");
		// @dokos: the default focus lands on "Assigned To", whose dropdown then covers the form
		this.dialog.focus_on_first_input = () => this.focus_first_empty_field();

		if (!can_write) {
			this.dialog.get_primary_btn().prop("disabled", true);
		}
		this.dialog.add_custom_action(__("Edit Full Form"), () => this.open_full_form());
		if (!this.is_new && frappe.model.can_delete(DOCTYPE)) {
			this.dialog.add_custom_action(__("Delete"), () => this.delete(), "btn-danger");
		}

		// Ctrl/Cmd + Enter saves
		this.dialog.$wrapper.on("keydown", (e) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !frappe.request.ajax_count) {
				e.preventDefault();
				this.dialog.get_primary_btn().trigger("click");
			}
		});

		if (this.doc?.start_time || this.doc?.end_time) {
			this.dialog.set_alert(
				__(
					"This assignment uses start and end times. Saving it here converts it to a daily duration.",
					null,
					"PM View"
				),
				"warning"
			);
		}
	}

	/**
	 * @dokos: focus the first empty required field that does not open a popup on focus
	 * (multiselect, link and date dropdowns would cover the form) — usually the duration.
	 */
	focus_first_empty_field() {
		const popup_types = ["Table MultiSelect", "Link", "Date", "Datetime", "Check"];
		const field = this.dialog.fields_list.find(
			(f) =>
				f.df.reqd &&
				!popup_types.includes(f.df.fieldtype) &&
				!f.get_value() &&
				typeof f.set_focus === "function"
		);
		field?.set_focus();
	}

	get_fields() {
		const refresh = frappe.utils.debounce(() => this.on_inputs_change(), 250);
		return [
			{
				fieldtype: "Table MultiSelect",
				fieldname: "assigned_to",
				label: __("Assigned To"),
				options: "Task Assignment Row",
				reqd: 1,
				onchange: refresh,
			},
			{
				fieldtype: "Link",
				fieldname: "project",
				label: __("Project"),
				options: "Project",
				reqd: 1,
				get_query: () => ({ filters: { status: "Open" } }),
				onchange: () => this.on_project_change(),
			},
			{
				fieldtype: "Link",
				fieldname: "task",
				label: __("Task"),
				options: "Task",
				get_query: () => {
					const filters = [
						["status", "not in", ["Cancelled", "Template"]],
						["is_template", "=", 0],
					];
					const project = this.dialog?.get_value("project");
					if (project) filters.push(["project", "=", project]);
					return { filters };
				},
				onchange: () => this.on_task_change(),
			},
			{ fieldtype: "Column Break" },
			{
				fieldtype: "Link",
				fieldname: "activity_type",
				label: __("Activity Type"),
				options: "Activity Type",
				get_query: () => ({ filters: { disabled: 0 } }),
				onchange: () => this.render_recent_types(),
			},
			{ fieldtype: "HTML", fieldname: "recent_types_html" },
			{ fieldtype: "Section Break" },
			{
				fieldtype: "Date",
				fieldname: "start_date",
				label: __("Start Date"),
				reqd: 1,
				onchange: refresh,
			},
			{ fieldtype: "Column Break" },
			{
				fieldtype: "Date",
				fieldname: "end_date",
				label: __("End Date"),
				reqd: 1,
				onchange: refresh,
			},
			{ fieldtype: "Column Break" },
			{
				fieldtype: "Duration",
				fieldname: "duration",
				label: __("Duration per day", null, "PM View"),
				reqd: 1,
				hide_days: 1,
				hide_seconds: 1,
				onchange: () => {
					this.render_presets();
					this.render_availability();
				},
			},
			{ fieldtype: "Section Break" },
			{ fieldtype: "HTML", fieldname: "presets_html" },
			{ fieldtype: "HTML", fieldname: "availability_html" },
			{
				fieldtype: "Check",
				fieldname: "skip_non_working_days",
				label: __("Skip holidays and approved leave", null, "PM View"),
				default: 1,
				onchange: () => {
					this.render_presets();
					this.render_availability();
				},
			},
			{
				// @dokos: also ToDo-assign the task to the employees' users
				fieldtype: "Check",
				fieldname: "add_to_task_assignees",
				label: __("Add to the task's Assigned To", null, "PM View"),
				default: 1,
				depends_on: "eval:doc.task",
				hidden: !frappe.model.can_write("Task"),
			},
		];
	}

	async set_initial_values() {
		const src = this.doc || this.values;
		const employees = this.doc
			? (this.doc.assigned_to || []).map((r) => r.employee)
			: this.values.employees || [];
		const values = {
			project: src.project,
			task: src.task,
			activity_type: src.activity_type,
			start_date: src.start_date,
			end_date: src.end_date || src.start_date,
			duration: src.duration,
		};
		await this.dialog.set_values(
			Object.fromEntries(Object.entries(values).filter(([, v]) => v != null && v !== ""))
		);
		if (employees.length) {
			await this.dialog.set_value(
				"assigned_to",
				employees.filter(Boolean).map((employee) => ({ employee }))
			);
		}
	}

	// -------------------------------------------------------------------------------
	// Values
	// -------------------------------------------------------------------------------

	get_employees() {
		const rows = this.dialog.get_value("assigned_to") || [];
		return [...new Set(rows.map((r) => r.employee).filter(Boolean))];
	}

	get_days() {
		const start = this.dialog.get_value("start_date");
		const end = this.dialog.get_value("end_date");
		if (!start || !end || end < start) return [];
		return daysBetween(start, end);
	}

	get_duration_minutes() {
		return Math.round((Number(this.dialog.get_value("duration")) || 0) / 60);
	}

	/** Days on which no selected employee is available (capacity 0 for all of them). */
	get_non_working_days() {
		const employees = this.get_employees();
		const capacity = this.availability?.capacity;
		if (!capacity || !employees.length) return new Set();
		return new Set(
			this.get_days().filter((day) =>
				employees.every((emp) => capacity[emp] && !(capacity[emp][day] > 0))
			)
		);
	}

	/** Contiguous [start, end] ranges of the period, without the non-working days. */
	get_working_ranges() {
		const off = this.get_non_working_days();
		const ranges = [];
		let current = null;
		for (const day of this.get_days()) {
			if (off.has(day)) {
				current = null;
				continue;
			}
			if (current && moment(current[1]).add(1, "day").format("YYYY-MM-DD") === day) {
				current[1] = day;
			} else {
				current = [day, day];
				ranges.push(current);
			}
		}
		return ranges;
	}

	get_planned_days() {
		const days = this.get_days();
		if (!this.dialog.get_value("skip_non_working_days")) return days;
		const off = this.get_non_working_days();
		return days.filter((d) => !off.has(d));
	}

	/** Minutes of this assignment (as saved) per employee and day, to exclude from the load. */
	own_minutes(employee, day) {
		if (!this.doc) return 0;
		const assigned = (this.doc.assigned_to || []).some((r) => r.employee === employee);
		if (!assigned || day < this.doc.start_date || day > this.doc.end_date) return 0;
		return Math.round((Number(this.doc.duration) || 0) / 60);
	}

	/** Full day of the first employee: highest capacity over the period (fallback 8h). */
	get_full_day_minutes() {
		const [first] = this.get_employees();
		const per_day = (first && this.availability?.capacity?.[first]) || {};
		const days = this.get_days();
		const start = this.dialog.get_value("start_date");
		const on_start = per_day[start];
		if (on_start > 0) return on_start;
		const max = Math.max(0, ...days.map((d) => per_day[d] || 0));
		return max || DEFAULT_DAY_MINUTES;
	}

	// -------------------------------------------------------------------------------
	// Events
	// -------------------------------------------------------------------------------

	on_inputs_change() {
		this.render_presets();
		this.refresh_availability();
	}

	async on_project_change() {
		const project = this.dialog.get_value("project");
		const task = this.dialog.get_value("task");
		if (task && project) {
			const task_project = await frappe.db.get_value("Task", task, "project");
			if (task_project?.message?.project && task_project.message.project !== project) {
				this.dialog.set_value("task", "");
			}
		}
	}

	async on_task_change() {
		const task = this.dialog.get_value("task");
		if (!task || this.dialog.get_value("project")) return;
		const res = await frappe.db.get_value("Task", task, "project");
		if (res?.message?.project) {
			this.dialog.set_value("project", res.message.project);
		}
	}

	// -------------------------------------------------------------------------------
	// Rendering
	// -------------------------------------------------------------------------------

	async render_recent_types() {
		const wrapper = this.dialog.fields_dict.recent_types_html.$wrapper[0];
		if (!this._recent_types) {
			this._recent_types = await this.fetch_recent_types();
		}
		const [colors, current] = [await getActivityTypeColors(), this.dialog.get_value("activity_type")];
		wrapper.replaceChildren();
		if (!this._recent_types.length) return;

		const group = h("div", {
			class: "pm-recent-types",
			attrs: { role: "group", "aria-label": __("Recent activity types", null, "PM View") },
		});
		group.append(h("span", { class: "dk-muted", text: __("Recent:", null, "PM View") }));
		for (const type of this._recent_types) {
			group.append(
				h(
					"button",
					{
						type: "button",
						class: "pm-type-chip",
						attrs: { "aria-pressed": type === current ? "true" : "false" },
						on: { click: () => this.dialog.set_value("activity_type", type) },
					},
					swatch(activityColor(type, colors[type])),
					__(type)
				)
			);
		}
		wrapper.append(group);
	}

	async fetch_recent_types() {
		try {
			const rows = await frappe.db.get_list(DOCTYPE, {
				fields: ["activity_type"],
				filters: [
					["owner", "=", frappe.session.user],
					["activity_type", "is", "set"],
				],
				order_by: "modified desc",
				limit: 50,
			});
			return [...new Set(rows.map((r) => r.activity_type).filter(Boolean))].slice(
				0,
				RECENT_TYPES_COUNT
			);
		} catch (e) {
			return [];
		}
	}

	render_presets() {
		const wrapper = this.dialog.fields_dict.presets_html.$wrapper[0];
		const full = this.get_full_day_minutes();
		const presets = [
			{ label: __("1h", null, "PM View"), minutes: 60 },
			{ label: __("2h", null, "PM View"), minutes: 120 },
			{ label: __("Half day", null, "PM View"), minutes: Math.round(full / 2) },
			{ label: __("Full day", null, "PM View"), minutes: full },
		];
		const current = this.get_duration_minutes();
		const group = h("div", {
			class: "pm-presets",
			attrs: { role: "group", "aria-label": __("Duration presets", null, "PM View") },
		});
		for (const preset of presets) {
			// @dokos: es-buttons (façade); a toggle, so the pressed one is aria-pressed
			group.append(
				erpnext.dokos.ui.button({
					label: preset.label,
					size: "xs",
					title: formatMinutes(preset.minutes),
					css_class: "pm-preset",
					attrs: { "aria-pressed": current === preset.minutes ? "true" : "false" },
					onclick: () => this.dialog.set_value("duration", preset.minutes * 60),
				})[0]
			);
		}

		const days = this.get_planned_days().length;
		const employees = Math.max(1, this.get_employees().length);
		const total = current * days;
		const total_label = h(
			"span",
			{ class: "pm-total-duration" },
			__("Total Duration", null, "PM View") + " ",
			h("b", { text: formatMinutes(total) })
		);
		if (employees > 1) {
			total_label.append(" " + __("per person", null, "PM View"));
		}
		wrapper.replaceChildren(h("div", { class: "pm-presets-row" }, group, total_label));
	}

	async refresh_availability() {
		const employees = this.get_employees();
		const days = this.get_days();
		if (!employees.length || !days.length) {
			this.availability = null;
			this.render_presets();
			this.render_availability();
			return;
		}
		const seq = ++this._availability_seq;
		try {
			const [res] = await Promise.all([
				frappe.xcall(AVAILABILITY_METHOD, {
					employees,
					start: days[0],
					end: days[days.length - 1],
				}),
				this.fetch_employee_names(employees),
			]);
			if (seq !== this._availability_seq) return; // a newer request is on its way
			this.availability = res;
		} catch (e) {
			this.availability = null;
		}
		this.render_presets();
		this.render_availability();
	}

	render_availability() {
		const wrapper = this.dialog.fields_dict.availability_html.$wrapper[0];
		wrapper.replaceChildren();
		const employees = this.get_employees();
		const days = this.get_days();
		if (!this.availability || !employees.length || !days.length) return;

		const minutes = this.get_duration_minutes();
		const skip = this.dialog.get_value("skip_non_working_days");
		const off = this.get_non_working_days();
		const shown = days.slice(0, MAX_AVAILABILITY_DAYS);
		const names = this.get_employee_names();
		const panel = h("div", { class: "pm-availability" });
		let overbooked = 0;

		for (const emp of employees) {
			const capacity = this.availability.capacity?.[emp] || {};
			const load = this.availability.load?.[emp] || {};
			const max = Math.max(0, ...shown.map((d) => capacity[d] || 0));
			panel.append(
				h("div", {
					class: "pm-availability-title",
					text: __("{0} · availability", [names[emp] || emp], "PM View"),
				})
			);
			const grid = h("div", { class: "pm-availability-grid", attrs: { role: "list" } });
			for (const day of shown) {
				const cap = capacity[day] || 0;
				const planned = skip && off.has(day) ? 0 : minutes;
				const projected = (load[day] || 0) - this.own_minutes(emp, day) + planned;
				const over = planned > 0 && projected > cap;
				overbooked += over ? 1 : 0;
				let note = "";
				if (cap === 0) note = __("Day off", null, "PM View");
				else if (cap < max) note = __("Partial day", null, "PM View");

				const ratio = cap ? Math.min(100, Math.round((projected / cap) * 100)) : projected ? 100 : 0;
				const label = `${formatMinutes(projected)} / ${formatMinutes(cap)}`;
				grid.append(
					h(
						"div",
						{
							class: ["pm-availability-day", over && "is-over", cap === 0 && "is-off"],
							attrs: { role: "listitem" },
						},
						h("span", { class: "pm-availability-date", text: formatDay(day, "weekdayDay") }),
						h(
							"span",
							{ class: "pm-bar", attrs: { "aria-hidden": "true" } },
							h("span", { class: "pm-bar-fill", style: { width: `${ratio}%` } })
						),
						h("span", {
							class: "pm-availability-value",
							text: note ? `${label} · ${note}` : label,
						})
					)
				);
			}
			panel.append(grid);
		}
		if (days.length > shown.length) {
			panel.append(
				h("div", {
					class: "dk-muted pm-availability-note",
					text: __("Only the first {0} days are shown.", [MAX_AVAILABILITY_DAYS], "PM View"),
				})
			);
		}
		if (overbooked) {
			// @dokos: façade alert (red = needs action), was a hand-built red box
			panel.append(
				erpnext.dokos.ui.alert({
					theme: "red",
					title: __(
						"Over capacity on {0} day(s): the assignees already have other assignments, a holiday or leave.",
						[overbooked],
						"PM View"
					),
					css_class: "pm-availability-warning",
				})[0]
			);
		}
		wrapper.append(panel);
	}

	get_employee_names() {
		return Object.fromEntries(
			this.get_employees().map((emp) => [emp, frappe.utils.get_link_title("Employee", emp) || emp])
		);
	}

	/** Load the missing employee names into the link title cache. */
	async fetch_employee_names(employees) {
		const missing = employees.filter((emp) => !frappe.utils.get_link_title("Employee", emp));
		await Promise.all(missing.map((emp) => frappe.utils.fetch_link_title("Employee", emp)));
	}

	// -------------------------------------------------------------------------------
	// Actions
	// -------------------------------------------------------------------------------

	get_doc_values() {
		const v = this.dialog.get_values();
		if (!v) return null;
		return {
			project: v.project,
			task: v.task || null,
			activity_type: v.activity_type || null,
			start_date: v.start_date,
			end_date: v.end_date,
			duration: v.duration,
			start_time: null,
			end_time: null,
			assigned_to: this.get_employees().map((employee) => ({ employee })),
		};
	}

	async save() {
		const values = this.get_doc_values();
		if (!values) return;
		if (!values.assigned_to.length) {
			frappe.msgprint(__("Please select at least one employee", null, "PM View"));
			return;
		}
		if (values.end_date < values.start_date) {
			frappe.msgprint(__("The end date must be after the start date"));
			return;
		}
		if (!(Number(values.duration) > 0)) {
			frappe.msgprint(__("Please set a duration per day", null, "PM View"));
			return;
		}

		let ranges = [[values.start_date, values.end_date]];
		if (this.dialog.get_value("skip_non_working_days")) {
			await this.refresh_availability(); // fresh capacity for the final dates
			ranges = this.get_working_ranges();
			if (!ranges.length) {
				frappe.msgprint(
					__(
						"No selected employee works in this period. Uncheck {0} to plan it anyway.",
						[__("Skip holidays and approved leave", null, "PM View")],
						"PM View"
					)
				);
				return;
			}
		}

		this.dialog.disable_primary_action();
		const saved = [];
		try {
			const [first, ...others] = ranges;
			if (this.is_new) {
				saved.push(await this.insert({ ...values, start_date: first[0], end_date: first[1] }));
			} else {
				const doc = { ...this.doc, ...values, start_date: first[0], end_date: first[1] };
				doc.assigned_to = values.assigned_to;
				saved.push(await frappe.xcall("frappe.client.save", { doc }));
			}
			for (const [start, end] of others) {
				saved.push(await this.insert({ ...values, start_date: start, end_date: end }));
			}
			this.dialog.hide();
			await this.add_to_task_assignees(values);
			frappe.show_alert({
				message:
					saved.length > 1
						? __(
								"{0} Task Assignments saved (non-working days skipped)",
								[saved.length],
								"PM View"
						  )
						: __("Saved"),
				indicator: "green",
			});
			this.on_saved?.(saved);
		} catch (e) {
			// Some split assignments may already exist: close to avoid saving them twice.
			if (saved.length) {
				this.dialog.hide();
				this.on_saved?.(saved);
			}
			throw e;
		} finally {
			this.dialog.enable_primary_action();
		}
	}

	/** @dokos: "Add to the task's Assigned To": the assignment is saved even if this fails. */
	async add_to_task_assignees(values) {
		if (!values.task || !this.dialog.get_value("add_to_task_assignees")) return;
		if (!frappe.model.can_write("Task")) return;
		try {
			const { without_user = [] } = await frappe.xcall(ASSIGN_TASK_METHOD, {
				task: values.task,
				employees: values.assigned_to.map((r) => r.employee),
			});
			if (without_user.length) {
				const names = without_user.map((e) =>
					frappe.utils.escape_html(frappe.utils.get_link_title("Employee", e) || e)
				);
				frappe.show_alert(
					{
						message: __(
							"{0} has no user: not added to the task's Assigned To",
							[names.join(", ")],
							"PM View"
						),
						indicator: "orange",
					},
					8
				);
			}
		} catch (e) {
			console.error(e);
		}
	}

	insert(values) {
		return frappe.db.insert({ doctype: DOCTYPE, ...values });
	}

	delete() {
		frappe.confirm(__("Permanently delete {0}?", [this.name]), async () => {
			await frappe.xcall("frappe.client.delete", { doctype: DOCTYPE, name: this.name });
			this.dialog.hide();
			frappe.show_alert({ message: __("Deleted"), indicator: "green" });
			this.on_saved?.(null);
		});
	}

	open_full_form() {
		const values = this.dialog.get_values(true) || {};
		this.dialog.hide();
		if (!this.is_new) {
			frappe.set_route("Form", DOCTYPE, this.name);
			return;
		}
		const doc = frappe.model.get_new_doc(DOCTYPE);
		for (const key of ["project", "task", "activity_type", "start_date", "end_date", "duration"]) {
			if (values[key]) doc[key] = values[key];
		}
		for (const employee of this.get_employees()) {
			frappe.model.add_child(doc, "assigned_to").employee = employee;
		}
		frappe.set_route("Form", DOCTYPE, doc.name);
	}
}
